import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'react-feather';
import { COLORS } from '../../../../utils/colors';
import { DillButton, DillModal } from '../../../general/Dill';
import { twMerge } from 'tailwind-merge';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { openDillToast } from '../../../../utils/helpers';
import {
  creditSafeCompanySearch,
  directorReport,
  getCreditSafeReport,
} from '../../../../redux/services/creditReportService';
import { ArrowsDownUp, SortAscending, SortDescending } from 'phosphor-react';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { getSupplierCreditApplication } from '../../../../redux/services/creditApplicationsService';
import { AppPagination } from '../../../general/AppPaginateFooter/AppPaginateFooter';

type CreditReport = {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  telephone: string;
  confidence: string | number | null;
  tradelines: string | number | null;
  status?: string;
  safeNo?: string;
  regNo?: string;
  id2?: string;
};
const CreditSafeReportCompanyModal = ({
  open,
  handleClose,
  data,
  onLearnMore,
  error,
  creditApplicationId,
  internalFormSubmissionId,
  onReportsChanged,
  companyDetails,
  defaultTotal,
  selectedTab,
  overlayZIndex,
}: {
  open: boolean;
  handleClose: () => void;
  data: CreditReport[];
  onLearnMore: (data: any, id: string) => void;
  error: string;
  creditApplicationId: string;
  // On an internal form's review page: reports are kept on that submission, and
  // onReportsChanged reloads them instead of refetching a credit application.
  internalFormSubmissionId?: string;
  onReportsChanged?: () => void;
  companyDetails: {
    companyName: string;
    address?: string;
    city?: string;
    state?: string;
    telephone?: string;
    zipCode?: string;
  };
  defaultTotal: number;
  selectedTab: null | 'business' | 'personal';
  overlayZIndex?: number;
}) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState('');
  const [sortedData, setSortedData] = useState<CreditReport[]>([]);
  const [sort, setSort] = useState('');
  const [sorted, setSorted] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [queryText, setQueryText] = useState('');
  const [errorText, setErrorText] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [modifyCompany, setModifyCompany] = useState(false);
  const [creditSafePage, setCreditSafePage] = useState<number>(1);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [companySearchModified, setCompanySearchModified] = useState(false);

  const columns = useMemo(() => {
    return [
      { id: 'companyName', name: 'Company Name', width: 120, cellType: 'VIEW_TEXT' },
      { id: 'address', name: 'Address', width: 140, cellType: 'VIEW_TEXT' },
      { id: 'city', name: 'City', width: 100, cellType: 'VIEW_TEXT' },
      { id: 'state', name: 'State', width: 100, cellType: 'VIEW_TEXT' },
      { id: 'zipCode', name: 'Zip Code', width: 80, cellType: 'VIEW_TEXT' },
      { id: 'status', name: 'Status', width: 80, cellType: 'VIEW_TEXT' },
      { id: 'action', name: '', width: 100, cellType: 'VIEW_TEXT' },
    ];
  }, []);

  useEffect(() => {
    if (open) {
      setTotalResults(defaultTotal);
    }
  }, [defaultTotal, open]);

  const totalWidth = useMemo(
    () =>
      columns.reduce((curr, prev) => {
        return curr + prev.width;
      }, 0),
    [columns]
  );

  const handleCloseModal = () => {
    setLoading('');
    setQueryText('');
    setSortedData([]);
    setModifyCompany(false);
    setCompanySearchModified(false);
    setTotalResults(0);
    setCreditSafePage(1);
    handleClose();
  };

  useEffect(() => {
    setErrorText(error);
  }, [error]);

  useEffect(() => {
    if (!data) return;

    setSortedData(data);
  }, [data]);

  const sortByProperty = (
    sortType: 'asc' | 'desc',
    sortKey: string,
    getter: (item: any) => any,
    compare?: (a: any, b: any) => number
  ) => {
    const filteredData = data.filter((report) => {
      const value = getter(report);
      return value !== undefined && value !== null;
    });

    const sortedDataCopy = [...filteredData];
    sortedDataCopy.sort((a, b) => {
      const valueA = getter(a);
      const valueB = getter(b);

      if (compare) {
        return compare(valueA, valueB) * (sortType === 'asc' ? 1 : -1);
      } else {
        const stringA = String(valueA).toUpperCase();
        const stringB = String(valueB).toUpperCase();

        if (stringA < stringB) {
          return sortType === 'asc' ? -1 : 1;
        } else if (stringA > stringB) {
          return sortType === 'asc' ? 1 : -1;
        }

        return 0;
      }
    });

    const undefinedOrNullReleases = data.filter((report) => {
      const value = getter(report);
      return value === undefined || value === null;
    });

    const sortedReportData = sortedDataCopy.concat(undefinedOrNullReleases);

    setSortedData(sortedReportData);
  };

  const handleSort = (value: string) => {
    if (data) {
      if (sort === value) {
        if (sorted) {
          setSortedData(data);
          setSort('');
          setSorted(false);
        } else {
          if (value === 'companyName') {
            sortByProperty('desc', value, (report) => report.name);
          } else if (value === 'address') {
            sortByProperty('desc', value, (report) => report.addressLine1);
          } else if (value === 'city') {
            sortByProperty('desc', value, (report) => report.city);
          } else if (value === 'state') {
            sortByProperty('desc', value, (report) => report.state);
          } else if (value === 'zipCode') {
            sortByProperty('desc', value, (report) => report.zipCode);
          } else if (value === 'status') {
            sortByProperty('desc', value, (report) => report.status);
          }

          setSorted(true);
        }
      } else {
        if (value === 'companyName') {
          sortByProperty('asc', value, (report) => report.name);
        } else if (value === 'address') {
          sortByProperty('asc', value, (report) => report.addressLine1);
        } else if (value === 'city') {
          sortByProperty('asc', value, (report) => report.city);
        } else if (value === 'state') {
          sortByProperty('asc', value, (report) => report.state);
        } else if (value === 'zipCode') {
          sortByProperty('asc', value, (report) => report.zipCode);
        } else if (value === 'status') {
          sortByProperty('asc', value, (report) => report.status);
        }

        setSort(value);
        setSorted(false);
      }
    }
  };

  const handleSearch = async () => {
    setLoadingList(true);
    const result = await dispatch(
      creditSafeCompanySearch({
        supplierId: currentSupplier?.id ?? '',
        companyDetails: { companyName: queryText, page: creditSafePage },
      })
    );
    setCompanySearchModified(true);
    setTotalResults(0);
    setCreditSafePage(1);

    if (result.type === 'creditReport/creditSafeCompanySearch/fulfilled') {
      const payload: any = result.payload;
      setTotalResults(payload?.data?.totalSize ? Number(payload?.data?.totalSize) : 1);
      const payloadData = payload?.data?.companies.map((item: any) => ({
        id: item.id,
        name: item.name,
        addressLine1: item.address.street,
        addressLine2: '',
        city: item.address.city,
        state: item.address.province,
        zipCode: item.address.postCode,
        telephone: '',
        confidence: null,
        tradelines: null,
        status: item.status,
        safeNo: item.safeNo,
        regNo: item.regNo,
        id2: item.id2,
      }));
      setSortedData(payloadData);
    } else {
      const payload: any = result.payload;
      const payloadErrorText =
        typeof payload === 'string' ? payload : 'Failed to get credit report.';
      setErrorText(payloadErrorText);
      setSortedData([]);
    }
    setLoadingList(false);
  };

  const handleLoadMore = async (page: number) => {
    setCreditSafePage(page);
    setLoadingList(true);
    let result = null;
    if (companySearchModified) {
      result = await dispatch(
        creditSafeCompanySearch({
          supplierId: currentSupplier?.id ?? '',
          companyDetails: { companyName: queryText, page },
        })
      );
    } else {
      result = await dispatch(
        creditSafeCompanySearch({
          supplierId: currentSupplier?.id ?? '',
          companyDetails: { ...companyDetails, page },
        })
      );
    }

    if (result.type === 'creditReport/creditSafeCompanySearch/fulfilled') {
      const payload: any = result.payload;
      setTotalResults(payload?.data?.totalSize ? Number(payload?.data?.totalSize) : 1);
      const payloadData = payload?.data?.companies.map((item: any) => ({
        id: item.id,
        name: item.name,
        addressLine1: item.address.street,
        addressLine2: '',
        city: item.address.city,
        state: item.address.province,
        zipCode: item.address.postCode,
        telephone: '',
        confidence: null,
        tradelines: null,
        status: item.status,
        safeNo: item.safeNo,
        regNo: item.regNo,
        id2: item.id2,
      }));
      setSortedData(payloadData);
    } else {
      const payload: any = result.payload;
      const payloadErrorText =
        typeof payload === 'string' ? payload : 'Failed to get credit report.';
      setErrorText(payloadErrorText);
      setSortedData([]);
    }
    setLoadingList(false);
  };

  const handleLearnMore = async (id: string) => {
    setLoading(id);
    let result = null;
    if (selectedTab === 'personal') {
      result = await dispatch(
        directorReport({
          peopleId: id,
          supplierId: currentSupplier?.id ?? '',
          // connectId: id,
          // name: companyDetails.companyName,
          // type: 'business',
          // creditApplicationId,
        })
      );
    } else {
      result = await dispatch(
        getCreditSafeReport({
          supplierId: currentSupplier?.id ?? '',
          connectId: id,
          name: companyDetails.companyName,
          type: 'business',
          creditApplicationId,
          internalFormSubmissionId,
        })
      );
    }

    if (result.type.includes('fulfilled')) {
      const payload: any = result.payload;
      const payloadData = payload?.data.htmlData ?? '';

      onLearnMore(payloadData, id);
      if (onReportsChanged) onReportsChanged();
      else
        dispatch(
          getSupplierCreditApplication({
            supplierId: currentSupplier?.id || '',
            creditApplicationId,
            hashedType: 'encrypted',
            fetchType: 'detailed',
            skipLoading: 'true',
          })
        );
    } else {
      const payload: any = result.payload;
      openDillToast({
        message: typeof payload === 'string' ? payload : 'Failed to get credit report.',
        type: 'ERROR',
      });
    }
    setLoading('');
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Credit Safe Company List"
      maxWidth="80vw"
      overlayZIndex={overlayZIndex}
      footer={
        <div className="flex justify-end w-full">
          <DillButton label="Close" variant="secondary" onClick={handleCloseModal} />
        </div>
      }>
      <div className="flex flex-col gap-2">
        {sortedData.length === 0 && (
          <div className="w-full flex flex-col justify-center items-center p-4 gap-2">
            {!loadingList && (
              <div className="text-base ERROR_500-CLR font-semibold">{errorText}</div>
            )}
            {!loadingList && (
              <div className="text-sm GREY_500-CLR font-semibold">
                Please try again using a different search.
              </div>
            )}
            <div className="mt-4 w-1/3 gap-2 flex flex-col justify-center items-center">
              <AppInputField
                id="comapanyName"
                value={queryText}
                placeholder="Enter Company Name"
                onTextChange={(text) => setQueryText(text)}
              />
              <DillButton
                label="Search"
                onClick={handleSearch}
                isLoading={loadingList}
                isDisabled={loadingList}
                width={80}
                height={40}
              />
            </div>
          </div>
        )}
        {sortedData.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex py-2 w-full">
              {modifyCompany && (
                <div className="flex flex-col w-1/3">
                  <div className="text-sm font-semibold">Modify Company Name search</div>
                  <div className="gap-2 flex justify-center items-center">
                    <AppInputField
                      id="comapanyName"
                      placeholder="Enter Company Name"
                      value={queryText}
                      onTextChange={(text) => setQueryText(text)}
                    />
                    <DillButton
                      label="Search"
                      onClick={handleSearch}
                      isLoading={loadingList}
                      isDisabled={loadingList}
                      width={80}
                      height={40}
                    />
                    {!loadingList && (
                      <DillButton
                        variant="ghost"
                        onClick={() => setModifyCompany(false)}
                        icon={<X size={18} color={COLORS.ERROR_500} />}
                        width={40}
                        height={40}
                      />
                    )}
                  </div>
                </div>
              )}
              {!modifyCompany && (
                <DillButton
                  label="Modify search"
                  variant="secondary"
                  onClick={() => setModifyCompany(true)}
                  isLoading={loadingList}
                  isDisabled={loadingList}
                  height={35}
                />
              )}
            </div>
            <div className="border rounded relative">
              {loadingList && (
                <div className="absolute inset-0 z-10 rounded backdrop-blur-sm bg-white/40 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                </div>
              )}
              <div className="w-full flex border-b justify-between px-4 sticky top-0 PRIMARY_50-BG items-center z-1">
                {columns.map((column, i) => (
                  <div
                    key={column.id + '-report-header' + i}
                    style={{ flex: column.width / totalWidth }}
                    className={twMerge('flex py-3 mx-0.5 cursor-pointer items-center')}
                    onClick={() => handleSort(column.id)}>
                    <p className="text-xs TEXT_SECONDARY-CLR">{column.name}</p>
                    {column.id !== 'action' && (
                      <div>
                        {sort === column.id && sorted ? (
                          <SortAscending color={COLORS.GREY_400} />
                        ) : sort === column.id && !sorted ? (
                          <SortDescending color={COLORS.GREY_400} />
                        ) : (
                          <ArrowsDownUp color={COLORS.GREY_400} />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="max-h-[500px] overflow-scroll">
                {sortedData.map((company, index) => {
                  const row = {
                    companyName: company.name,
                    address: [company.addressLine1, company.addressLine2]
                      .filter(Boolean)
                      .join(', '),
                    city: company.city,
                    state: company.state,
                    zipCode: company.zipCode,
                    status: company.status,
                  };

                  return (
                    <div
                      key={index + 'oo' + company.id}
                      className="w-full flex items-center px-4 justify-between border-b border-b-gray-200">
                      {columns.map((column, i) => (
                        <div
                          key={column.id}
                          style={{ flex: column.width / totalWidth }}
                          className={`flex py-3 mx-0.5 ${
                            columns.length - 1 !== i ? 'overflow-hidden' : ''
                          }`}>
                          {column.id !== 'action' && (
                            <p className="capitalize w-full text-xs">
                              {row[column.id as keyof typeof row]}
                            </p>
                          )}
                          {column.id === 'action' && (
                            <DillButton
                              label="View Report"
                              variant="secondary"
                              onClick={() => handleLearnMore(company.id)}
                              isLoading={loading === company.id}
                              isDisabled={loading !== ''}
                              height={35}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
            {totalResults > 0 && (
              <AppPagination
                total={totalResults}
                perPage={100}
                label="Credit Safe Results"
                page={creditSafePage}
                onPageChange={handleLoadMore}
              />
            )}
          </div>
        )}
      </div>
    </DillModal>
  );
};

export default CreditSafeReportCompanyModal;
